import * as turf from '@turf/turf'
import WebMercatorViewport from 'viewport-mercator-project'

import { IAddressBounds, IGlobeViewStyle } from '~/client/graph/types.generated'

import { resolveGlobeViewStyle } from './mapboxConstants'

// The PURE half of the camera arithmetic. The orchestration — flyTo, writing `viewport` — needs
// the map and stays on the store; moving it to a class holding a store reference would be motion,
// not decoupling.

// Taken FROM the resolver rather than listed here: the resolver already enumerates the whole
// style because it is what the app applies, so a field added to the schema is compared the moment
// it is resolved and there is no second list to forget.
const comparedStyleKeys = Object.keys(
  resolveGlobeViewStyle(null),
) as (keyof IGlobeViewStyle)[]

// Both sides go through the resolver first. A saved view predating a toggle stores it as null,
// and the app applies the resolver's default rather than the null — so comparing a raw saved
// style against a resolved live one reported a difference that the user could not see and could
// not clear, leaving the reset control enabled on an unchanged view. The old inline version
// papered over one case of this with a lone `!!`.
export function isSameGlobeViewStyle(
  saved: IGlobeViewStyle | null | undefined,
  current: IGlobeViewStyle | null | undefined,
): boolean {
  const left = resolveGlobeViewStyle(saved)
  const right = resolveGlobeViewStyle(current)
  return comparedStyleKeys.every(key => left[key] === right[key])
}

export interface ICameraPose {
  latitude: number
  longitude: number
  zoom: number
  bearing: number
  pitch: number
}

// Four decimals: the camera is written back from mapbox's own move events, so an identical view
// round-trips with float noise well below what anyone can see. Pitch too: a tilted view saves
// whatever angle the tilt left, and comes back from mapbox a few ulps off.
const CAMERA_PRECISION = 4

const CAMERA_KEYS: Array<keyof ICameraPose> = [
  'latitude',
  'longitude',
  'zoom',
  'bearing',
  'pitch',
]

export function isSameCamera(a: ICameraPose, b: ICameraPose): boolean {
  return CAMERA_KEYS.every(
    key =>
      a[key].toFixed(CAMERA_PRECISION) === b[key].toFixed(CAMERA_PRECISION),
  )
}

// The zoom that frames a sitemap's bounds in the current viewport. Note the corner order: the
// bounds are given sw/ne but fitBounds wants the box corners, so the latitudes cross over.
export function sitemapFitZoom(
  bounds: IAddressBounds,
  width: number,
  height: number,
): number {
  return new WebMercatorViewport({ width, height }).fitBounds(
    [
      [bounds.sw.lng, bounds.ne.lat],
      [bounds.ne.lng, bounds.sw.lat],
    ],
    {},
  ).zoom
}

// A longer flight for a longer journey, so a jump across the site does not read as a teleport
// and a nudge next door does not crawl.
export function flyOutDuration(
  baseMs: number,
  from: { longitude: number; latitude: number },
  to: { longitude: number; latitude: number },
): number {
  const distance = turf.distance(
    [to.longitude, to.latitude],
    [from.longitude, from.latitude],
  )
  return baseMs + distance / 2
}
